import { createContext, PropsWithChildren, useContext, useRef, useState } from 'react'
import {
  TitleOption,
  TitleOptionsContext as ITitleOptionsContext,
  Values,
} from 'components/Wizards/MultiStepBlogPostWizard/Types'
import { ourUuid } from 'utils'
import { useMutation } from 'react-query'
import { generateTitles } from 'components/Wizards/generateTitles'
import { GeneratePrompt } from 'types'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'

const TitleOptionsContext = createContext<ITitleOptionsContext>({
  abortGeneration: () => {},
  titleOptions: [],
  generateTitleOptions: () => Promise.reject('Not implemented'),
  updateTitleOptions: () => {},
  updateTitleOption: () => {},
  addTitleOption: () => {},
  isLoading: false,
})

export const TitleOptionsProvider = ({ children }: PropsWithChildren) => {
  const workspaceId = useCurrentWorkspaceId()
  const [titleOptions, setTitleOptions] = useState<Array<TitleOption>>([])
  const { mutateAsync, isLoading } = useMutation(generateTitles)

  const abortController = useRef<AbortController | null>(null)
  if (abortController.current === null) abortController.current = new AbortController()

  const abortGeneration = () => {
    abortController.current?.abort()
    abortController.current = new AbortController()
  }

  const addTitleOption = () => {
    setTitleOptions((oldTitleOptions) => {
      return [...oldTitleOptions, [ourUuid(), '']]
    })
  }

  const updateTitleOption = (newTitleOption: TitleOption) => {
    setTitleOptions((oldTitleOptions) => {
      return oldTitleOptions.map((t) => {
        if (t[0] === newTitleOption[0]) return newTitleOption
        return t
      })
    })
  }

  const generateTitleOptions = async (payload: Values) => {
    setTitleOptions([])
    const prompt: GeneratePrompt = { ...payload, generation_type: ChannelType.BlogPost }
    const options = await mutateAsync({
      payload: {
        prompt,
        workspaceId: workspaceId,
      },
      signal: abortController.current?.signal,
    })
    const titleOptions: Array<TitleOption> = options.map((t) => [ourUuid(), t])
    setTitleOptions(titleOptions)
    return titleOptions
  }

  return (
    <TitleOptionsContext.Provider
      value={{
        abortGeneration,
        titleOptions,
        generateTitleOptions,
        updateTitleOptions: setTitleOptions,
        updateTitleOption,
        addTitleOption,
        isLoading,
      }}
    >
      {children}
    </TitleOptionsContext.Provider>
  )
}

export const useTitleOptions = () => useContext(TitleOptionsContext)
